'use client'

import {
  useEffect,
  useState,
  useSyncExternalStore,
  useTransition,
} from 'react'
import { useRouter } from '@/i18n/navigation'
import {
  getTabShipperId,
  syncShipperCookie,
} from '@/app/[locale]/_utils/tab-shipper'
import Loading from '../../loading'

const MAX_REFRESH_ATTEMPTS = 2

// sessionStorage has no change events within the same document; read once per render.
const subscribe = () => () => {}

interface ShipperRenderGuardProps {
  /** Shipper the server rendered this content for. */
  serverShipperId: string | null
  children: React.ReactNode
}

/**
 * Last line of defence against showing another shipper's menu: if the server
 * rendered this page for a shipper other than the one this WebView belongs to
 * (shared cookie overwritten by another mini-app), hide it, fix the cookie and
 * re-render. Gives up after a couple of attempts so it can never blank the page.
 */
export default function ShipperRenderGuard({
  serverShipperId,
  children,
}: ShipperRenderGuardProps) {
  const router = useRouter()
  const [attempts, setAttempts] = useState(0)
  const [isRefreshing, startTransition] = useTransition()
  const tabShipperId = useSyncExternalStore(
    subscribe,
    getTabShipperId,
    () => null
  )

  const mismatch =
    Boolean(tabShipperId && serverShipperId) &&
    tabShipperId !== serverShipperId
  const canRetry = mismatch && attempts < MAX_REFRESH_ATTEMPTS

  useEffect(() => {
    if (!canRetry || isRefreshing || !tabShipperId) return

    let cancelled = false
    syncShipperCookie(tabShipperId).then(() => {
      if (cancelled) return
      setAttempts((value) => value + 1)
      startTransition(() => router.refresh())
    })

    return () => {
      cancelled = true
    }
  }, [canRetry, isRefreshing, tabShipperId, router])

  if (canRetry || (mismatch && isRefreshing)) {
    return <Loading text='Page is loading...' />
  }

  return <>{children}</>
}
